import React from "react";

type StatsCardProps = {
  label: string;
  value: string | number;
  icon?: React.ReactNode | string;
  variant?: "neutral" | "brand";
};

/** Small stat card used on Admin dashboards. */
export default function StatsCard({
  label,
  value,
  icon,
  variant = "neutral",
}: StatsCardProps) {
  const isBrand = variant === "brand";

  const cardClasses = isBrand
    ? "rounded-2xl border border-[#214D3C] bg-[#214D3C] p-5 text-[#FEF6D6] shadow-[0_25px_60px_-35px_rgba(33,77,60,0.65)]"
    : "rounded-2xl border border-gray-200 bg-white p-5 text-gray-900 shadow-sm";

  const iconClasses = isBrand
    ? "flex h-10 w-10 items-center justify-center rounded-xl border border-[#FEF6D6]/40 bg-transparent text-[#FEF6D6]"
    : "flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-700";

  const labelClasses = isBrand
    ? "text-xs uppercase tracking-wide text-[#FEF6D6]/70"
    : "text-xs uppercase tracking-wide text-gray-500";

  const valueClasses = isBrand
    ? "mt-1 text-2xl font-semibold text-[#FEF6D6]"
    : "mt-1 text-2xl font-semibold text-gray-900";

  const renderIcon = () => {
    if (!icon) {
      return (
        <div className={iconClasses} aria-hidden>
          •
        </div>
      );
    }

    if (typeof icon === "string") {
      return (
        <div className={iconClasses}>
          <img
            src={icon}
            alt=""
            className="h-5 w-5 object-contain"
            style={
              isBrand
                ? {
                    filter:
                      "brightness(0) saturate(100%) invert(96%) sepia(7%) saturate(726%) hue-rotate(330deg) brightness(104%) contrast(93%)",
                  }
                : undefined
            }
            aria-hidden
          />
        </div>
      );
    }

    return <div className={iconClasses}>{icon}</div>;
  };

  return (
    <div className={cardClasses}>
      <div className="flex items-center gap-3">
        {renderIcon()}
        <div>
          <div className={labelClasses}>{label}</div>
          <div className={valueClasses}>{value}</div>
        </div>
      </div>
    </div>
  );
}
